/* eslint-disable import/prefer-default-export */
import Decimal from 'decimal.js';
import { isNumber } from 'lodash';
import { datadogLogs } from '@datadog/browser-logs';
import { convert as newConvert } from '@syngenta-digital/package-typescript-cropwise-financials-uom';
import { DEFAULT_ROUND, Maths } from './maths';
import {
  AreaStdUnits,
  AreaOtherUnits,
  AreaAllUnits,
  MassAndWeightStdUnits,
  MassAndWeightOtherUnits,
  MassAndWeightAllUnits,
  VolumeStdUnits,
  VolumeOtherUnits,
  VolumeAllUnits,
  getCorrectUnitAbbreviation,
  getCorrectUnitName,
  ImperialUnits,
  MetricUnits,
  getCorrectUnitToConversion
} from './unitsAbbreviation';
import alphanumSort from './sorters';

const resolveConvert = convertFn => convertFn || newConvert;

export const specialCaseUnit = [
  'bag',
  'box',
  'bulk',
  'bushel',
  'can',
  'crate',
  'dispenser',
  'kernal',
  'kernel',
  'load',
  'package',
  'plant',
  'roll',
  'seed',
  'tablet',
  'tray',
  'unit'
];

const invalidUnits = ['kg', 'feet', 'cubic feet', 'square feet'];
const SERVICE_UNITS = ['second', 'hour', 'day', 'year'];

/*
  https://www.omnicalculator.com/physics/specific-gravity
  we are expecting the density is in grams per cubic centimeter g/cm3
*/

const otherUnits = [
  ...AreaOtherUnits,
  ...MassAndWeightOtherUnits,
  ...VolumeOtherUnits
];
const stdUnits = [...AreaStdUnits, ...MassAndWeightStdUnits, ...VolumeStdUnits];

const calculateSpecificGravity = density => (density ? density / 1.0 : 0);

const cleanCompatibleUnits = possibilities => {
  const possibleUnitConversions = [];

  possibilities.forEach(unit => {
    if (!unit.endsWith('s') && !invalidUnits.includes(unit.toLowerCase())) {
      if (unit?.toLowerCase() === 'acre inch') {
        possibilities.push('acreinch');
        return;
      }
      possibleUnitConversions.push(unit);
    }
  });

  return possibleUnitConversions;
};
export const sortingImperialUnitsAndMetricUnits = units => {
  const imperialUnitValues = [];
  const metricUnitValues = [];
  const otherGenericUnits = [];

  units.forEach(unit => {
    const imperialUnitExist = ImperialUnits.indexOf(unit.key) !== -1;
    const metricUnitExist = MetricUnits.indexOf(unit.key) !== -1;

    if (imperialUnitExist) {
      imperialUnitValues.push(unit);
    }
    if (metricUnitExist) {
      metricUnitValues.push(unit);
    }
    if (!imperialUnitExist && !metricUnitExist) {
      otherGenericUnits.push(unit);
    }
  });
  return imperialUnitValues.concat(metricUnitValues, otherGenericUnits);
};
const unitAbbreviation = units => {
  const transformedUnits = units.map(unit => ({
    key: unit,
    value: getCorrectUnitAbbreviation(unit)
  }));

  const valueMap = transformedUnits.reduce((acc, item) => {
    if (!acc[item.value]) {
      acc[item.value] = [];
    }
    acc[item.value].push(item.key);
    return acc;
  }, {});

  const uniqueUnits = transformedUnits.filter(item => {
    const relatedKeys = valueMap[item.value];
    const hasDuplicate = relatedKeys.length > 1;
    const isInOtherUnits = otherUnits.includes(item.key);
    const isInStdUnits = stdUnits.includes(item.key);
    return !(hasDuplicate && isInOtherUnits && !isInStdUnits);
  });
  return sortingImperialUnitsAndMetricUnits(alphanumSort(uniqueUnits, 'value'));
};

const removeDuplicates = units => [...new Set(units)];

export const getCompatibleUnits = (units, density, convertFn) => {
  const mainUnit = units[0];
  const packageUnit = units[1];
  const possibilities = [
    getCorrectUnitName(mainUnit),
    getCorrectUnitName(packageUnit)
  ];
  if (AreaAllUnits.includes(mainUnit) && AreaAllUnits.includes(packageUnit)) {
    possibilities.push(...AreaStdUnits);
    return unitAbbreviation(removeDuplicates(possibilities));
  }

  if (SERVICE_UNITS.includes(mainUnit)) {
    possibilities.push(...SERVICE_UNITS);
    return unitAbbreviation(removeDuplicates(possibilities));
  }

  const isVolume = VolumeAllUnits.includes(mainUnit);
  const isMass = MassAndWeightAllUnits.includes(mainUnit);
  // Handle standard conversion
  if (density > 0 && (isVolume || isMass)) {
    possibilities.push(...VolumeStdUnits, ...MassAndWeightStdUnits);
  } else if (isVolume) {
    possibilities.push(...VolumeStdUnits);
  } else if (isMass) {
    possibilities.push(...MassAndWeightStdUnits);
  } else {
    const convertUnits = resolveConvert(convertFn)(
      0,
      calculateSpecificGravity(density)
    )
      .from(mainUnit)
      .possibilities();
    possibilities.push(...cleanCompatibleUnits(convertUnits));
  }

  return unitAbbreviation(removeDuplicates(possibilities));
};

export const roundValue = valueToRound =>
  Math.round((valueToRound + Number.EPSILON) * 100) / 100;

export const getConvertUnitFromTo = (
  value,
  fromUnit,
  toUnit,
  { density, productDensity, stdfactor, stdunit, stdpackageunit } = {},
  convertFn
) => {
  try {
    if (value === 0) return { Value: 0 };

    if (!value || !fromUnit || !toUnit) {
      return { Value: 1 }; // ????
    }

    if (fromUnit === toUnit) return { Value: value };

    const actualDensity = density || productDensity;

    if (specialCaseUnit.includes(stdpackageunit)) {
      if (fromUnit === stdpackageunit) {
        const valueInStdUnit = Maths.multiplyRound(
          value,
          stdfactor,
          DEFAULT_ROUND,
          Decimal.ROUND_HALF_UP
        );
        if (toUnit === stdunit) {
          return { Value: valueInStdUnit };
        }
        const conversion = resolveConvert(convertFn)(
          valueInStdUnit,
          actualDensity
        )
          .from(getCorrectUnitToConversion(stdunit))
          .to(getCorrectUnitToConversion(toUnit))
          .toJSON();
        return conversion;
      }
      if (toUnit === stdpackageunit) {
        if (fromUnit === stdunit) {
          return {
            Value: Maths.divideSafeRound(
              value,
              stdfactor,
              DEFAULT_ROUND,
              Decimal.ROUND_HALF_UP
            )
          };
        }
        const conversionToStdUnit = resolveConvert(convertFn)(
          value,
          actualDensity
        )
          .from(getCorrectUnitToConversion(fromUnit))
          .to(getCorrectUnitToConversion(stdunit))
          .toJSON();
        return {
          Value: Maths.divideSafeRound(
            conversionToStdUnit.Value,
            stdfactor,
            DEFAULT_ROUND,
            Decimal.ROUND_HALF_UP
          )
        };
      }
    }

    return resolveConvert(convertFn)(value, actualDensity)
      .from(getCorrectUnitToConversion(fromUnit))
      .to(getCorrectUnitToConversion(toUnit))
      .toJSON();
  } catch (error) {
    datadogLogs.logger.error('Error in getConvertUnitFromTo', error, {
      fromUnit,
      toUnit,
      value,
      density,
      productDensity,
      stdfactor,
      stdunit,
      stdpackageunit
    });
    throw error;
  }
};

export const setConversionFactor = (areSame, valueToInvert) =>
  areSame ? 1 / valueToInvert : valueToInvert;

export const calcSpecialCase = (
  value,
  fromUnit,
  toUnit,
  product,
  convertFn
) => {
  const fromUnitIsStdUnit = unitsAreSame(fromUnit, product.stdunit);
  const fromUnitIsPackageUnit = unitsAreSame(fromUnit, product.stdpackageunit);
  const toUnitIsStdUnit = unitsAreSame(toUnit, product.stdunit);
  const toUnitIsPackageUnit = unitsAreSame(toUnit, product.stdpackageunit);
  const conversionFactor = product.stdfactor;

  const areUnitsSame = unitsAreSame(fromUnit, toUnit);
  let resultConversion = value;
  if (!value || areUnitsSame) {
    return resultConversion;
  }
  if (
    !fromUnitIsPackageUnit &&
    !toUnitIsPackageUnit &&
    !fromUnitIsStdUnit &&
    !toUnitIsStdUnit
  ) {
    return getConvertUnitFromTo(value, fromUnit, toUnit, product, convertFn)
      .Value;
  }
  if (fromUnitIsPackageUnit) {
    resultConversion = Maths.multiplyRound(
      value,
      conversionFactor,
      DEFAULT_ROUND,
      Decimal.ROUND_HALF_UP
    );
  } else {
    resultConversion = getConvertUnitFromTo(
      value,
      fromUnit,
      product.stdunit,
      product,
      convertFn
    ).Value;
  }

  if (toUnitIsPackageUnit) {
    resultConversion = Maths.divideSafeRound(
      resultConversion,
      conversionFactor,
      DEFAULT_ROUND,
      Decimal.ROUND_HALF_UP
    );
  } else if (!toUnitIsStdUnit) {
    resultConversion = getConvertUnitFromTo(
      resultConversion,
      product.stdunit,
      toUnit,
      product,
      convertFn
    ).Value;
  }
  return resultConversion;
};

export const unitsAreSame = (firstUnit, secondUnit) => firstUnit === secondUnit;

export const calcConversions = (
  convertValue,
  fromUnit,
  toUnit,
  bothUnitsAreSame,
  product,
  convertFn
) => {
  let converted = 0;
  if (bothUnitsAreSame) {
    converted = convertValue;
  } else {
    const totalConversion = getConvertUnitFromTo(
      Number.parseFloat(convertValue),
      fromUnit,
      toUnit,
      product,
      convertFn
    );
    converted = totalConversion?.Value;
  }

  return converted;
};

const byRatePerArea = ['ratePerAreaValue', 'ByRatePerArea'];
const byTotalProduct = ['totalProductValue', 'ByTotalProduct'];
const byRatePerTank = ['ratePerTankValue', 'ByRatePerTank'];

/**
 * @param {string} valueChanged
 * @param {number} targetValue
 * @param {object|null} changedUnits
 * @param {object|null} changedAppArea
 * @param {number|null} newPrice
 * @param {object} productToAdd
 * @param {{ values?: object, convertFn?: Function }} [options]
 */
export const calculateProductValues = (
  valueChanged,
  targetValue,
  changedUnits,
  changedAppArea,
  newPrice,
  productToAdd,
  { values = {}, convertFn } = {}
) => {
  let totalProduct = 0;
  let ratePerTank = 0;
  let ratePerArea = 0;
  let totalCost = 0;
  const appArea = changedAppArea?.changedArea || productToAdd.appliedAreaValue;
  const appPct = changedAppArea?.changedPct || productToAdd.coveragePercent;
  const rateUnitValue = changedUnits?.rateUnit || productToAdd.ratePerAreaUnit;
  const totalProductUnitValue =
    changedUnits?.totalProdUnit || productToAdd.totalProductUnit;
  const tankUnitValue = changedUnits?.tankUnit || productToAdd.ratePerTankUnit;
  const priceBag =
    newPrice ||
    productToAdd.priceValue ||
    productToAdd.averagePriceAtTimeOfCreation;
  const priceUnitToUse =
    productToAdd.priceUnit ||
    productToAdd.averagePriceUnitAtTimeOfCreation ||
    productToAdd.stdpackageunit;
  const tankCount = values.tankInformation?.tankCount;

  const totalUnitIsUnitPrice = unitsAreSame(
    totalProductUnitValue,
    priceUnitToUse
  );

  const specialCase = specialCaseUnit.includes(productToAdd.stdpackageunit);

  if (byRatePerArea.includes(valueChanged)) {
    const totalUnitIsSame = unitsAreSame(rateUnitValue, totalProductUnitValue);
    const tankUnitIsSame = unitsAreSame(totalProductUnitValue, tankUnitValue);
    // Calculate total product from rate per area and applied area
    // This unit will be in rate unit initially
    const totalProductInRatePerAreaUnit = multiply(targetValue, appArea);

    if (specialCase) {
      // Convert total product to the same unit for total product unit
      totalProduct = calcSpecialCase(
        totalProductInRatePerAreaUnit,
        rateUnitValue,
        totalProductUnitValue,
        productToAdd,
        convertFn
      );
      // Calculate rate per tank in the same unit for total product
      const ratePerTankInTotalProductUnit = divide(totalProduct, tankCount);

      // Convert rate per tank to the same unit as tank unit
      ratePerTank = calcSpecialCase(
        ratePerTankInTotalProductUnit,
        totalProductUnitValue,
        tankUnitValue,
        productToAdd,
        convertFn
      );
    } else {
      totalProduct = calcConversions(
        totalProductInRatePerAreaUnit,
        rateUnitValue,
        totalProductUnitValue,
        totalUnitIsSame,
        productToAdd,
        convertFn
      );

      if (tankCount > 0) {
        const rateTank = divide(totalProduct, tankCount);
        ratePerTank = calcConversions(
          rateTank,
          totalProductUnitValue,
          tankUnitValue,
          tankUnitIsSame,
          productToAdd,
          convertFn
        );
      }
    }

    ratePerArea = targetValue;
  }

  if (byTotalProduct.includes(valueChanged)) {
    const rateUnitIsSame = unitsAreSame(totalProductUnitValue, rateUnitValue);
    const tankUnitIsSame = unitsAreSame(totalProductUnitValue, tankUnitValue);

    if (specialCase) {
      // Calculate rate per area in the same unit for total product
      const ratePerAreaInProductUnit = divide(targetValue, appArea);

      // Convert rate per area to the same unit as rate unit
      ratePerArea = calcSpecialCase(
        ratePerAreaInProductUnit,
        totalProductUnitValue,
        rateUnitValue,
        productToAdd,
        convertFn
      );
      // Calculate rate per tank in the same unit for total product
      const ratePerTankInTotalProductUnit = divide(targetValue, tankCount);
      // Convert rate per tank to the same unit as tank unit
      ratePerTank = calcSpecialCase(
        ratePerTankInTotalProductUnit,
        totalProductUnitValue,
        tankUnitValue,
        productToAdd,
        convertFn
      );
    } else {
      if (appArea > 0) {
        const rateArea = divide(targetValue, appArea);

        ratePerArea = calcConversions(
          rateArea,
          totalProductUnitValue,
          rateUnitValue,
          rateUnitIsSame,
          productToAdd,
          convertFn
        );
      }

      if (tankCount > 0) {
        const rateTank = divide(targetValue, tankCount);

        ratePerTank = calcConversions(
          rateTank,
          totalProductUnitValue,
          tankUnitValue,
          tankUnitIsSame,
          productToAdd,
          convertFn
        );
      }
    }

    totalProduct = targetValue;
  }

  if (byRatePerTank.includes(valueChanged)) {
    if (tankCount > 0) {
      const rateUnitIsSame = unitsAreSame(tankUnitValue, rateUnitValue);
      const totalUnitIsSame = unitsAreSame(
        tankUnitValue,
        totalProductUnitValue
      );
      // Calculate total product from rate per tank and tank count
      // This unit will be in tank unit initially
      const totalProductInTankUnit = multiply(targetValue, tankCount);

      if (specialCase) {
        // Calculate rate per area in the same unit for total product
        const ratePerAreaInTankUnit = divide(totalProductInTankUnit, appArea);

        // Convert rate per area to the same unit as rate unit
        ratePerArea = calcSpecialCase(
          ratePerAreaInTankUnit,
          tankUnitValue,
          rateUnitValue,
          productToAdd,
          convertFn
        );

        totalProduct = calcSpecialCase(
          totalProductInTankUnit,
          tankUnitValue,
          totalProductUnitValue,
          productToAdd,
          convertFn
        );
      } else {
        if (appArea > 0) {
          const rateArea = divide(totalProductInTankUnit, appArea);

          ratePerArea = calcConversions(
            rateArea,
            tankUnitValue,
            rateUnitValue,
            rateUnitIsSame,
            productToAdd,
            convertFn
          );
        }

        totalProduct = calcConversions(
          totalProductInTankUnit,
          tankUnitValue,
          totalProductUnitValue,
          totalUnitIsSame,
          productToAdd,
          convertFn
        );
      }
    }

    ratePerTank = targetValue;
  }

  if (totalUnitIsUnitPrice) {
    totalCost = totalProduct * priceBag;
  } else {
    const totalProductConverted = calcConversions(
      totalProduct,
      totalProductUnitValue,
      priceUnitToUse,
      totalUnitIsUnitPrice,
      productToAdd,
      convertFn
    );
    totalCost = multiply(totalProductConverted, priceBag);
  }

  return {
    ...productToAdd,
    totalProductValue: isNumber(totalProduct)
      ? Maths.parseFloatRound(
          totalProduct,
          DEFAULT_ROUND,
          Decimal.ROUND_HALF_UP
        )
      : totalProduct,
    ratePerAreaValue: isNumber(ratePerArea)
      ? Maths.parseFloatRound(ratePerArea, DEFAULT_ROUND, Decimal.ROUND_HALF_UP)
      : ratePerArea,
    ratePerTankValue: isNumber(ratePerTank)
      ? Maths.parseFloatRound(ratePerTank, DEFAULT_ROUND, Decimal.ROUND_HALF_UP)
      : ratePerTank,
    totalCostAtTimeOfCreation: isNumber(totalCost)
      ? Maths.parseFloatRound(totalCost, DEFAULT_ROUND, Decimal.ROUND_HALF_UP)
      : totalCost,
    averagePriceAtTimeOfCreation: priceBag,
    averagePriceUnitAtTimeOfCreation: priceUnitToUse,
    priceValue: priceBag,
    priceUnit: priceUnitToUse,
    ratePerAreaUnit: rateUnitValue,
    ratePerTankUnit: tankUnitValue,
    totalProductUnit: totalProductUnitValue,
    appliedAreaValue: appArea,
    coveragePercent: appPct
  };
};

export const calculateNumberOfApplications = (
  productToAdd,
  convertFn,
  numberOfApps = 1
) => {
  const {
    appliedAreaValue,
    ratePerAreaValue,
    totalProductUnit,
    averagePriceAtTimeOfCreation,
    stdunit,
    stdpackageunit,
    stdfactor,
    density,
    productDensity,
    ratePerAreaUnit
  } = productToAdd;

  let totalProductValue = ratePerAreaValue * appliedAreaValue * numberOfApps;

  if (ratePerAreaUnit !== stdunit) {
    const conversionToStdUnit = getConvertUnitFromTo(
      totalProductValue,
      ratePerAreaUnit,
      stdunit,
      { density, productDensity, stdfactor, stdunit, stdpackageunit },
      convertFn
    ).Value;
    totalProductValue = conversionToStdUnit;
  }

  if (stdunit !== totalProductUnit) {
    const conversionToTotalProductUnit = getConvertUnitFromTo(
      totalProductValue,
      stdunit,
      totalProductUnit,
      { density, productDensity, stdfactor, stdunit, stdpackageunit },
      convertFn
    ).Value;
    totalProductValue = conversionToTotalProductUnit;
  }

  const totalCostAtTimeOfCreation =
    totalProductValue * averagePriceAtTimeOfCreation;

  return {
    ...productToAdd,
    applicationCount: numberOfApps,
    totalProductValue: roundValue(totalProductValue),
    totalCostAtTimeOfCreation: roundValue(totalCostAtTimeOfCreation)
  };
};

const divide = (a, b) => {
  if (b === 0) return 0;
  return a && b
    ? Maths.divideSafeRound(a, b, DEFAULT_ROUND, Decimal.ROUND_HALF_UP)
    : 0;
};

const multiply = (a, b) =>
  a && b ? Maths.multiplyRound(a, b, DEFAULT_ROUND, Decimal.ROUND_HALF_UP) : 0;
